import { useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const aboutPageJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "url": "https://cnyangji.cn/about",
    "name": "关于我们 - 色戒完整指南",
    "description": "了解色戒完整指南平台，包括品牌介绍、声誉展示与联系方式",
    "inLanguage": "zh-CN",
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://cnyangji.cn/",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "关于我们",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50 pt-20">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutPageJsonLd) }}></script>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}></script>
      <div className="max-w-4xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <div className="py-4">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Hero */}
        <section className="py-10 text-center">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-950 mb-4">
            关于色戒完整指南
          </h1>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            我们是一个专注电影艺术与文化传播的独立平台，致力于为影迷提供《色戒》无删减版的全面资讯与深度解读
          </p>
        </section>

        {/* Brand Introduction */}
        <section className="py-8 border-t border-background-200/70">
          <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-5">
            <i className="ri-building-2-line text-primary-500 mr-2"></i>
            品牌介绍
          </h2>
          <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
            <p>
              <strong className="text-foreground-900">色戒完整指南</strong>创立于2023年，是一个专注于华语经典电影深度解读与资源导航的内容平台。我们的团队由资深影评人、电影研究学者和热爱电影文化的内容创作者组成。
            </p>
            <p>
              我们深知，一部优秀的电影作品值得被完整地理解和欣赏。《色戒》作为李安导演的代表作，其艺术价值远超一般的商业电影。然而，由于各种原因，许多观众只能看到删减版本，难以感受导演完整的艺术表达。
            </p>
            <p>
              正因如此，我们创建了这个平台，希望通过对《色戒》无删减版的全面介绍——包括版本对比、剧情解析、创作背景和正版资源获取指南——帮助更多影迷深入了解这部杰作的完整面貌。
            </p>
            <p>
              我们坚持内容的专业性与客观性，每一篇文章都经过严格的资料核查和多轮编辑审校，确保为读者提供准确、有深度的资讯。同时，我们积极倡导和支持正版观影，鼓励观众通过合法渠道欣赏电影作品。
            </p>
          </div>
        </section>

        {/* Reputation / Stats */}
        <section className="py-8 border-t border-background-200/70">
          <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-5">
            <i className="ri-medal-line text-accent-500 mr-2"></i>
            平台声誉
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {[
              { value: '12+', label: '深度文章', icon: 'ri-article-line' },
              { value: '50万+', label: '累计阅读', icon: 'ri-eye-line' },
              { value: '98%', label: '用户好评', icon: 'ri-heart-line' },
              { value: '4.9', label: '内容评分', icon: 'ri-star-line' },
            ].map((stat, i) => (
              <div
                key={i}
                className="text-center p-5 rounded-lg bg-background-50 border border-background-200/70"
              >
                <i className={`${stat.icon} text-2xl text-primary-500 mb-2`}></i>
                <div className="text-2xl font-heading font-bold text-foreground-950 mb-1">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                <i className="ri-shield-check-line text-accent-500 mr-1.5"></i>
                专业团队
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们的内容团队包括电影学专业研究者、资深影评人和文化传播从业者，确保每一篇文章都具备专业深度与阅读价值。
              </p>
            </div>
            <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                <i className="ri-verified-badge-line text-primary-500 mr-1.5"></i>
                信息可靠
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                所有内容均经过多方资料交叉验证，引用的信息来源包括导演访谈、官方出版物及权威电影数据库，确保信息准确可靠。
              </p>
            </div>
            <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                <i className="ri-user-heart-line text-accent-500 mr-1.5"></i>
                以影迷为本
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们始终以影迷需求为中心，持续优化内容导航与阅读体验，帮助每一位访客快速找到所需信息。
              </p>
            </div>
            <div className="p-5 rounded-lg bg-background-50 border border-background-200/70">
              <h3 className="font-heading font-semibold text-foreground-950 mb-3 text-sm">
                <i className="ri-copyright-line text-primary-500 mr-1.5"></i>
                尊重版权
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                本站不提供任何影片下载链接，所有资源推荐均为正版渠道，尊重并维护电影创作者的合法权益。
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-8 border-t border-background-200/70">
          <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-5">
            <i className="ri-mail-line text-foreground-700 mr-2"></i>
            联系我们
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                如果您对本站内容有任何建议、意见或合作意向，欢迎通过以下方式与我们取得联系。我们会在收到信息后的1-2个工作日内回复。
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-sm text-foreground-700">
                  <i className="ri-mail-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>contact@cnyangji.cn</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground-700">
                  <i className="ri-map-pin-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>中国上海市徐汇区武康路376号</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-foreground-700">
                  <i className="ri-phone-line text-primary-500 text-lg w-5 h-5 flex items-center justify-center"></i>
                  <span>+86 21 6288 1234</span>
                </div>
              </div>
            </div>
            <form
              data-readdy-form="contact"
              className="space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const formData = new FormData(form);
                const honeypot = (formData.get('phone_alt') as string || '').trim();
                if (honeypot) {
                  setFormStatus('success');
                  setFormMessage('感谢您的留言，我们会尽快回复！');
                  return;
                }
                setFormStatus('submitting');
                setFormMessage('');
                try {
                  const body = new URLSearchParams();
                  formData.forEach((value, key) => {
                    if (key !== 'phone_alt') {
                      body.append(key, value as string);
                    }
                  });
                  const resp = await fetch('https://readdy.ai/api/form/d9sv9sd8o1vn02ekumk0', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body.toString(),
                  });
                  const responseText = await resp.text();
                  let parsed: any = {};
                  try { parsed = JSON.parse(responseText); } catch (_) {}
                  if (resp.ok && parsed?.code === 'OK') {
                    setFormStatus('success');
                    setFormMessage('感谢您的留言，我们会尽快回复！');
                    form.reset();
                  } else {
                    const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || '提交失败，请稍后重试';
                    if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
                      setFormStatus('success');
                      setFormMessage('感谢您的留言，我们会尽快回复！');
                    } else {
                      setFormStatus('error');
                      setFormMessage(serverMsg);
                    }
                  }
                } catch {
                  setFormStatus('error');
                  setFormMessage('网络错误，请稍后重试');
                }
              }}
            >
              <div>
                <input
                  type="text"
                  name="name"
                  placeholder="您的姓名"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  name="email"
                  placeholder="您的邮箱"
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                />
              </div>
              <div>
                <textarea
                  name="message"
                  placeholder="请输入您的留言内容（500字以内）"
                  rows={4}
                  maxLength={500}
                  required
                  className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200/70 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                ></textarea>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="spam-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {formStatus === 'submitting' ? '发送中...' : '发送留言'}
              </button>
              {formMessage && (
                <p className={`text-sm mt-3 ${formStatus === 'success' ? 'text-accent-700' : 'text-red-600'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </div>
        </section>
      </div>

      {/* Disclaimer */}
      <section className="mt-10 bg-background-100/50 py-8">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <h2 className="text-base font-heading font-semibold text-foreground-950 mb-3">
            免责声明
          </h2>
          <div className="text-xs text-foreground-500 leading-relaxed space-y-2">
            <p>
              本站为独立运营的电影资讯平台，与电影《色戒》的制作公司、发行方无任何隶属或合作关系。本站所有内容仅作影迷交流与信息参考之用。
            </p>
            <p>
              本站不提供任何影片下载链接、种子文件或盗版资源。我们积极倡导并支持正版观影，鼓励用户通过合法渠道欣赏电影作品。
            </p>
            <p>
              电影《色戒》及其相关元素的版权归原制作公司所有。本站使用的电影相关图片和名称仅用于内容说明目的，符合合理使用原则。
            </p>
            <p>
              如权利人认为本站内容侵犯了您的合法权益，请通过上述联系方式与我们沟通，我们将及时处理。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}